import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, BarChart, Bar, Cell
} from "recharts";
import { Users2, CheckCircle, XCircle, Clock, Timer, Calendar, ArrowRight } from "lucide-react";
import styles from "../hrms.module.scss";
import { APIConstant } from "services/api-constant";
import { useTheme } from "context/ThemeContext";

const Tip = ({ active, payload, label }) => {
  const { T } = useTheme();
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:8, padding:"10px 14px", fontFamily:"Outfit,sans-serif", fontSize:13 }}>
      <p style={{ color:T.muted, margin:"0 0 6px", fontSize:11 }}>{label}</p>
      {payload.map((p,i) => <p key={i} style={{ color:p.color, margin:"3px 0", fontWeight:600 }}>{p.name}: <span style={{ color:T.text }}>{p.value}</span></p>)}
    </div>
  );
};

export default function AttendanceDashboard() {
  const { T } = useTheme();
  const navigate = useNavigate();
  const [today,     setToday]     = useState({});
  const [trend,     setTrend]     = useState([]);
  const [upcoming,  setUpcoming]  = useState([]);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [tRes, sRes, hRes] = await Promise.all([
          axios.get(APIConstant.attendanceToday),
          axios.get(`${APIConstant.attendanceStats}?days=14`),
          axios.get(APIConstant.upcomingHolidays),
        ]);
        setToday(tRes.data || {});
        setTrend((sRes.data?.trend || []).slice(-10));
        setUpcoming(hRes.data || []);
      } catch(e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const STATS = [
    { label:"Total Employees",  val:today.total    ||0, color:"cyan",   sub:"active staff" },
    { label:"Present Today",    val:today.present  ||0, color:"green",  sub:"in office + WFH" },
    { label:"Absent",           val:today.absent   ||0, color:"red",    sub:"marked absent" },
    { label:"On Leave",         val:today.on_leave ||0, color:"amber",  sub:"leave requests" },
    { label:"Not Yet Marked",   val:today.not_marked||0,color:"purple", sub:"pending" },
  ];

  const TYPE_COLOR = { National:T.red, Regional:T.amber, Optional:T.muted, Company:T.cyan };

  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>TIME & ATTENDANCE</div>
          <h2>Attendance Dashboard</h2>
          <p>Overview of today's attendance and trends</p>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.btnSecondary} onClick={()=>navigate("/admin/hrms/leaves")}>
            Leave Requests
          </button>
          <button className={styles.btnPrimary} onClick={()=>navigate("/admin/hrms/tracker")}>
            <Clock size={14}/> Mark Attendance
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className={`${styles.statsGrid} ${styles.cols5}`}>
        {STATS.map(({label,val,color,sub})=>(
          <div key={label} className={`${styles.statCard} ${styles[color]}`}>
            <div className={styles.statVal}>{val}</div>
            <div className={styles.statLabel}>{label}</div>
            <div style={{fontSize:10.5,color:T.dim,marginTop:4}}>{sub}</div>
          </div>
        ))}
      </div>

      <div className={`${styles.contentRow} ${styles.col2}`}>
        {/* Trend chart */}
        <div className={styles.card}>
          <div className={styles.cardHeader}><h3>Attendance Trend</h3><span className={styles.cardMeta}>Last 14 days</span></div>
          <div className={styles.cardBody}>
            {loading ? <div className={styles.loader}><div className={styles.spinner}/></div> : (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={trend} margin={{top:5,right:5,left:-20,bottom:0}}>
                  <defs>
                    <linearGradient id="gp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={T.green} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={T.green} stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="ga" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={T.red} stopOpacity={0.2}/>
                      <stop offset="95%" stopColor={T.red} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.toggle_bg}/>
                  <XAxis dataKey="date" tick={{fill:T.muted,fontSize:10}} axisLine={false} tickLine={false}
                    tickFormatter={d => d.slice(5)}/>
                  <YAxis tick={{fill:T.muted,fontSize:11}} axisLine={false} tickLine={false}/>
                  <Tooltip content={<Tip/>}/>
                  <Area type="monotone" dataKey="present" name="Present" stroke={T.green} strokeWidth={2} fill="url(#gp)" dot={false}/>
                  <Area type="monotone" dataKey="absent"  name="Absent"  stroke={T.red} strokeWidth={2} fill="url(#ga)" dot={false}/>
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Upcoming holidays */}
        <div className={styles.card}>
          <div className={styles.cardHeader}><h3>Upcoming Holidays</h3>
            <button className={styles.btnSm} onClick={()=>navigate("/admin/hrms/holidays")}>View All <ArrowRight size={11}/></button>
          </div>
          <div className={styles.cardBody}>
            {upcoming.length === 0 ? (
              <div style={{textAlign:"center",padding:"30px",color:T.muted,fontSize:13}}>No upcoming holidays</div>
            ) : upcoming.map(h => {
              const d = new Date(h.date);
              return (
                <div key={h.id} style={{display:"flex",alignItems:"center",gap:14,padding:"12px 0",borderBottom:`1px solid ${T.cyan}0F`}}>
                  <div style={{width:44,height:44,borderRadius:10,background:`${T.amber}1A`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <div style={{fontSize:16,fontWeight:800,color:T.amber,lineHeight:1}}>{d.getDate()}</div>
                    <div style={{fontSize:9,color:T.amber,textTransform:"uppercase"}}>{d.toLocaleString("default",{month:"short"})}</div>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:500,color:T.text}}>{h.name}</div>
                    <div style={{fontSize:11.5,color:T.muted,marginTop:2}}>{d.toLocaleDateString("en-IN",{weekday:"long"})}</div>
                  </div>
                  <span style={{fontSize:10.5,padding:"2px 8px",borderRadius:20,background:`${TYPE_COLOR[h.type]||T.muted}20`,color:TYPE_COLOR[h.type]||T.muted}}>{h.type}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Quick links */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14}}>
        {[
          {label:"Daily Tracker",   desc:"Mark today's attendance",      icon:Clock,        route:"/admin/hrms/tracker",   color:T.cyan},
          {label:"Leave Requests",  desc:"Approve/reject pending leaves", icon:Calendar,    route:"/admin/hrms/leaves",    color:T.purple},
          {label:"Holiday Calendar",desc:"Manage company holidays",       icon:CheckCircle, route:"/admin/hrms/holidays",  color:T.amber},
          {label:"Leave Types",     desc:"Configure leave policies",      icon:Timer,       route:"/admin/hrms/leave-types",color:T.green},
        ].map(({label,desc,icon:Icon,route,color})=>(
          <div key={label} className={styles.card} style={{cursor:"pointer",transition:"transform 0.2s"}}
            onClick={()=>navigate(route)}
            onMouseEnter={e=>e.currentTarget.style.transform="translateY(-3px)"}
            onMouseLeave={e=>e.currentTarget.style.transform=""}>
            <div className={styles.cardBody} style={{display:"flex",flexDirection:"column",gap:10}}>
              <div style={{width:38,height:38,borderRadius:10,background:`${color}20`,display:"flex",alignItems:"center",justifyContent:"center",color}}>
                <Icon size={18}/>
              </div>
              <div>
                <div style={{fontSize:14,fontWeight:600,color:T.text,marginBottom:3}}>{label}</div>
                <div style={{fontSize:12,color:T.muted}}>{desc}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
